import { type ReactNode, useId, useState } from 'react';
import { Button } from '../../components/ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '../../components/ui/popover';
import { Separator } from '../../components/ui/separator';
import { Icon } from '../icon';

export interface BannerInboxAction {
  label: string;
  variant?: 'default' | 'secondary';
  onClick?: () => void;
  closeOnClick?: boolean;
}

export interface BannerInboxItem {
  id: string;
  title?: string;
  description: ReactNode;
  read: boolean;
  actions?: BannerInboxAction[];
}

export interface BannerInboxProps {
  title: string;
  closeLabel: string;
  emptyStateLabel: string;
  items: BannerInboxItem[];
  children: ReactNode;
}

export function BannerInboxBellIcon({ hasNotifications }: { hasNotifications: boolean }) {
  return <Icon name={hasNotifications ? 'bell-active' : 'bell'} size="lg" />;
}

function BannerInboxListItem({ item, onAction }: { item: BannerInboxItem; onAction: () => void }) {
  const actions = item.actions ?? [];

  return (
    <div className="flex w-full flex-col items-start gap-1">
      <div className="flex w-full items-center gap-1">
        {item.read ? null : <span className="size-2 shrink-0 rounded-full bg-primary-button" aria-hidden="true" />}
        {item.title ? <p className="min-w-px flex-1 font-body font-semibold text-base text-foreground">{item.title}</p> : null}
      </div>
      <div className="w-full font-body text-base text-subtle-foreground">{item.description}</div>
      {actions.length > 0 ? (
        <div className="flex w-full items-start gap-3 pt-2">
          {actions.map((action) => (
            <Button
              key={action.label}
              variant={action.variant === 'secondary' ? 'subtle' : 'default'}
              shape="pill"
              size="sm"
              className="flex-1"
              onClick={() => {
                action.onClick?.();
                if (action.closeOnClick !== false) {
                  onAction();
                }
              }}
            >
              {action.label}
            </Button>
          ))}
        </div>
      ) : null}
    </div>
  );
}

function BannerInboxList({
  items,
  emptyStateLabel,
  onAction,
}: Pick<BannerInboxProps, 'items' | 'emptyStateLabel'> & { onAction: () => void }) {
  if (items.length === 0) {
    return <p className="w-full py-2 font-body text-base text-subtle-foreground">{emptyStateLabel}</p>;
  }

  return (
    <div className="flex w-full flex-col items-start gap-3">
      {items.map((item, index) => (
        <div key={item.id} className="flex w-full flex-col items-start gap-3">
          {index > 0 ? <Separator decorative className="shrink-0 rounded-[3px] bg-border-subtle" /> : null}
          <BannerInboxListItem item={item} onAction={onAction} />
        </div>
      ))}
    </div>
  );
}

export function BannerInbox({ title, closeLabel, emptyStateLabel, items, children }: BannerInboxProps) {
  const [open, setOpen] = useState(false);
  const titleId = useId();

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>{children}</PopoverTrigger>
      <PopoverContent
        align="end"
        sideOffset={-40}
        collisionPadding={8}
        aria-labelledby={titleId}
        className="flex w-[var(--radix-popper-available-width)] flex-col gap-4 rounded-2xl border-0 p-4 shadow-popover sm:w-96"
      >
        <div className="flex w-full items-center gap-2">
          <button
            type="button"
            aria-label={closeLabel}
            onClick={() => setOpen(false)}
            className="flex shrink-0 cursor-pointer items-center justify-center"
          >
            <Icon name="arrow-left" size="lg" />
          </button>
          <h2 id={titleId} className="min-w-px flex-1 font-headline font-semibold text-foreground text-xl leading-7">
            {title}
          </h2>
        </div>
        <Separator decorative className="shrink-0 rounded-[3px] bg-border-subtle" />
        <BannerInboxList items={items} emptyStateLabel={emptyStateLabel} onAction={() => setOpen(false)} />
      </PopoverContent>
    </Popover>
  );
}
